// src/App.js
import React from "react";
import { Routes, Route, useNavigate, useParams, useLocation, Link, Navigate } from "react-router-dom";
import StockChart from "./components/StockChart";
import StockChartTradingView from "./components/StockChartTradingView";
import SearchBar from "./components/SearchBar";
import PortfolioPage from "./components/PortfolioPage";
import PredictionLab from "./components/PredictionLab";
import CalculationAssumptionsPage from "./components/CalculationAssumptionsPage";
import IntrinsicValuePage from "./components/IntrinsicValuePage";
import TutorialPage from "./components/TutorialPage";
import AuthPage from "./components/AuthPage";
import PlanSelectPage from "./components/PlanSelectPage";
import ProfilePage from "./components/ProfilePage";
import { PRIVACY_POLICY_CONTENT, TERMS_OF_SERVICE_CONTENT } from "./content/legal";

/* ===== Brand tokens - Modern Sleek Design ===== */
const BRAND = {
  bg: "#0a0a0f",
  bgSecondary: "#111118",
  panel: "#1a1a24",
  panelHover: "#22222f",
  border: "#2a2a3a",
  borderLight: "#3a3a4a",
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  muted: "#888898",
  accent: "#6366f1", // Modern indigo
  accentHover: "#818cf8",
  accentLight: "#a5b4fc",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  gradientAccent: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
};

function Shell({ children }) {
  return (
    <div style={{ 
      minHeight: "100vh", 
      background: BRAND.bg,
      backgroundImage: "radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)",
      color: BRAND.text,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Inter', 'Helvetica Neue', sans-serif"
    }}>
      {children}
    </div>
  );
}

// Landing header: Logo, Pricing (scroll), Login
function LandingHeader({ isMobile }) {
  const logoUrl = "/fs-logo.png";
  const linkStyle = {
    padding: isMobile ? "8px 12px" : "10px 18px",
    background: "transparent",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "8px",
    color: BRAND.text,
    fontSize: isMobile ? "13px" : "14px",
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "none",
    transition: "all 0.2s",
  };
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 10002,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: isMobile ? "12px 16px" : "16px 24px",
      background: "rgba(26, 26, 36, 0.95)",
      backdropFilter: "blur(20px)",
      borderBottom: `1px solid ${BRAND.border}`,
      boxShadow: "0 4px 24px rgba(0, 0, 0, 0.4)"
    }}>
      <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
        <img src={logoUrl} alt="Fundamental Signals" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ height: isMobile ? 40 : 48, objectFit: "contain" }} />
      </Link>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <a
          href="#pricing"
          style={linkStyle}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = BRAND.accent; e.currentTarget.style.color = BRAND.accentLight; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = BRAND.border; e.currentTarget.style.color = BRAND.text; }}
        >
          Pricing
        </a>
        <Link
          to="/guide"
          style={linkStyle}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = BRAND.accent; e.currentTarget.style.color = BRAND.accentLight; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = BRAND.border; e.currentTarget.style.color = BRAND.text; }}
        >
          User guide
        </Link>
        <Link
          to="/auth"
          style={{ ...linkStyle, background: BRAND.accent, borderColor: BRAND.accent }}
          onMouseEnter={(e) => { e.currentTarget.style.background = BRAND.accentHover; e.currentTarget.style.borderColor = BRAND.accentHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = BRAND.accent; e.currentTarget.style.borderColor = BRAND.accent; }}
        >
          Sign in / Sign up
        </Link>
      </div>
    </div>
  );
}

function Header() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const logoUrl = "/fs-logo.png";
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 10002,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: isMobile ? "12px 16px" : "16px 24px",
      background: "rgba(26, 26, 36, 0.95)",
      backdropFilter: "blur(20px)",
      borderBottom: `1px solid ${BRAND.border}`,
      boxShadow: "0 4px 24px rgba(0, 0, 0, 0.4)"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Link to="/" style={{ 
          display: "flex", 
          alignItems: "center", 
          textDecoration: "none", 
          cursor: "pointer",
          transition: "transform 0.2s ease",
        }}
        onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
        >
          <img
            src={logoUrl}
            alt="Fundamental Signals"
            onError={(e)=>{ e.currentTarget.style.display="none"; }}
            style={{ height: isMobile ? 40 : 48, objectFit: "contain" }}
          />
        </Link>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <TopRightLink isMobile={isMobile} />
        <LogoutButton isMobile={isMobile} />
      </div>
    </div>
  );
}

function LogoutButton({ isMobile = false }) {
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to sign out?")) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("authTimestamp");
      window.location.href = "/";
    }
  };

  return (
    <button
      onClick={handleLogout}
      style={{
        padding: isMobile ? "8px 12px" : "10px 16px",
        background: "transparent",
        border: `1px solid ${BRAND.border}`,
        borderRadius: "8px",
        color: BRAND.textSecondary,
        fontSize: isMobile ? "13px" : "14px",
        fontWeight: "500",
        cursor: "pointer",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => {
        e.target.style.borderColor = BRAND.danger;
        e.target.style.color = BRAND.danger;
      }}
      onMouseLeave={(e) => {
        e.target.style.borderColor = BRAND.border;
        e.target.style.color = BRAND.textSecondary;
      }}
    >
      Sign Out
    </button>
  );
}

// Default ticker when opening Fundamentals/Indicators from the top menu (e.g. from landing page)
const DEFAULT_TICKER = "AAPL";

function TopRightLink({ isMobile = false }) {
  const { ticker: routeTicker } = useParams();
  const location = useLocation();
  const ticker = routeTicker && routeTicker.length > 0 ? routeTicker : DEFAULT_TICKER;
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [navOpen, setNavOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const baseLinkStyle = {
    border: `1px solid ${BRAND.border}`,
    background: BRAND.panel,
    padding: isMobile ? "8px 12px" : "10px 20px",
    borderRadius: 12,
    textDecoration: "none",
    color: BRAND.text,
    fontWeight: 600,
    fontSize: isMobile ? "12px" : "14px",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
    whiteSpace: "nowrap",
    minHeight: "44px",
    display: "flex",
    alignItems: "center",
  };

  const activeLinkStyle = {
    ...baseLinkStyle,
    background: "rgba(99, 102, 241, 0.2)",
    borderColor: BRAND.accent,
    boxShadow: "0 0 0 1px rgba(99, 102, 241, 0.3)",
  };

  const hoverStyle = {
    background: BRAND.panelHover,
    borderColor: BRAND.accent,
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
  };

  const nav = (to, label) => {
    const active = location.pathname === to;
    const linkStyle = active ? activeLinkStyle : baseLinkStyle;
    return (
      <Link
        to={to}
        style={linkStyle}
        onMouseEnter={(e) => !isMobile && Object.assign(e.currentTarget.style, active ? { ...linkStyle, ...hoverStyle } : hoverStyle)}
        onMouseLeave={(e) => Object.assign(e.currentTarget.style, active ? activeLinkStyle : baseLinkStyle)}
      >
        {label}
      </Link>
    );
  };

  const dropdownItems = [
    { to: "/portfolio", label: "Portfolio" },
    { to: "/profile", label: "Profile" },
    { to: "/guide", label: isMobile ? "Guide" : "User guide" },
    { to: "/calculations", label: isMobile ? "Calculations" : "Calculation & Assumptions" },
    { to: "/intrinsic-value", label: isMobile ? "IV lab" : "Intrinsic value" },
    { to: "/ml", label: isMobile ? "Pred." : "Prediction Lab" },
  ];

  const navContent = (
    <>
      {nav(`/stock/${ticker}/indicators`, "Indicators")}
      {nav(`/stock/${ticker}/fundamentals`, "Fundamentals")}
      <div style={{ position: "relative" }} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          style={{
            ...baseLinkStyle,
            cursor: "pointer",
            borderColor: dropdownOpen ? BRAND.accent : undefined,
            background: dropdownOpen ? BRAND.panelHover : baseLinkStyle.background,
          }}
          onMouseEnter={(e) => {
            if (!isMobile) Object.assign(e.currentTarget.style, hoverStyle);
          }}
          onMouseLeave={(e) => {
            if (!dropdownOpen) Object.assign(e.currentTarget.style, baseLinkStyle);
          }}
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
        >
          More ▾
        </button>
        {dropdownOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: 6,
              minWidth: 200,
              background: BRAND.panel,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
              overflow: "hidden",
              zIndex: 10003,
            }}
          >
            {dropdownItems.map(({ to, label }, i) => (
              <Link
                key={to}
                to={to}
                style={{
                  display: "block",
                  padding: "12px 16px",
                  color: BRAND.text,
                  textDecoration: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  borderBottom: i < dropdownItems.length - 1 ? `1px solid ${BRAND.border}` : "none",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = BRAND.panelHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
                onClick={() => setDropdownOpen(false)}
              >
                {label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
        <button
          type="button"
          aria-label="Toggle menu"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((o) => !o)}
          style={{
            ...baseLinkStyle,
            cursor: "pointer",
            borderColor: navOpen ? BRAND.accent : undefined,
          }}
        >
          ☰
        </button>
        {navOpen && (
          <div
            style={{
              position: "fixed",
              top: 56,
              left: 16,
              right: 16,
              padding: 12,
              background: BRAND.panel,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
              zIndex: 10003,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {nav(`/stock/${ticker}/indicators`, "Indicators")}
            {nav(`/stock/${ticker}/fundamentals`, "Fundamentals")}
            {dropdownItems.map(({ to, label }) => (
              <Link key={to} to={to} style={{ ...baseLinkStyle, display: "block", textAlign: "center" }} onClick={() => setNavOpen(false)}>
                {label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>{navContent}</div>;
}

// Modal for Privacy Policy / Terms of Service
function LegalModal({ open, onClose, title, content }) {
  const bodyRef = React.useRef(null);
  const [showScrollTop, setShowScrollTop] = React.useState(false);

  React.useEffect(() => {
    if (!open || !bodyRef.current) return;
    const el = bodyRef.current;
    const onScroll = () => setShowScrollTop(el.scrollTop > 120);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [open]);

  const scrollToTop = () => bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10010,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: BRAND.panel,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 16,
          maxWidth: 640,
          maxHeight: "85vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${BRAND.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: BRAND.text }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: BRAND.textSecondary,
              fontSize: 24,
              cursor: "pointer",
              padding: "0 4px",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div
          ref={bodyRef}
          style={{
            padding: 24,
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            maxHeight: "70vh",
            whiteSpace: "pre-wrap",
            fontSize: 14,
            lineHeight: 1.6,
            color: BRAND.textSecondary,
          }}
        >
          {content}
        </div>
        {showScrollTop && (
          <div style={{ padding: "0 24px 16px", flexShrink: 0 }}>
            <button
              type="button"
              onClick={scrollToTop}
              style={{
                width: "100%",
                padding: "10px 16px",
                background: BRAND.panelHover,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 8,
                color: BRAND.textSecondary,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Scroll to top
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Footer({ isMobile }) {
  const [privacyOpen, setPrivacyOpen] = React.useState(false);
  const [termsOpen, setTermsOpen] = React.useState(false);
  const logoUrl = "/fs-logo.png";
  const linkStyle = {
    color: BRAND.textSecondary,
    textDecoration: "none",
    fontSize: isMobile ? 13 : 14,
    transition: "color 0.2s, border-color 0.2s",
    background: "none",
    border: "none",
    borderBottom: "1px solid transparent",
    cursor: "pointer",
    padding: "0 0 2px",
  };
  const linkHover = (e, over) => {
    e.currentTarget.style.color = over ? BRAND.accentLight : BRAND.textSecondary;
    e.currentTarget.style.borderBottomColor = over ? BRAND.accentLight : "transparent";
  };
  return (
    <>
      <footer style={{
        padding: isMobile ? "32px 16px" : "48px 24px",
        borderTop: `1px solid ${BRAND.border}`,
        background: BRAND.bgSecondary,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: isMobile ? 16 : 24,
      }}>
        <Link to="/" style={{ display: "flex", alignItems: "center" }}>
          <img src={logoUrl} alt="Fundamental Signals" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ height: 36, objectFit: "contain" }} />
        </Link>
        <span style={{ color: BRAND.muted, fontSize: isMobile ? 12 : 13 }} aria-hidden>|</span>
        <button type="button" style={linkStyle} onClick={() => setPrivacyOpen(true)} onMouseEnter={(e) => linkHover(e, true)} onMouseLeave={(e) => linkHover(e, false)}>Privacy Policy</button>
        <span style={{ color: BRAND.muted, fontSize: isMobile ? 12 : 13 }} aria-hidden>|</span>
        <button type="button" style={linkStyle} onClick={() => setTermsOpen(true)} onMouseEnter={(e) => linkHover(e, true)} onMouseLeave={(e) => linkHover(e, false)}>Terms of Service</button>
        <span style={{ color: BRAND.muted, fontSize: isMobile ? 12 : 13 }} aria-hidden>|</span>
        <Link to="/guide" style={{ ...linkStyle, border: "none", padding: 0 }} onMouseEnter={(e) => linkHover(e, true)} onMouseLeave={(e) => linkHover(e, false)}>User guide</Link>
        <span style={{ color: BRAND.muted, fontSize: isMobile ? 12 : 13 }} aria-hidden>|</span>
        <a href="mailto:fundamentalsignals@gmail.com" style={{ ...linkStyle, border: "none", padding: 0 }} onMouseEnter={(e) => linkHover(e, true)} onMouseLeave={(e) => linkHover(e, false)}>Support</a>
        <span style={{ color: BRAND.muted, fontSize: isMobile ? 12 : 13 }} aria-hidden>|</span>
        <a href="https://x.com/FundSignals" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" style={{ ...linkStyle, border: "none", padding: 0, display: "inline-flex", alignItems: "center" }} onMouseEnter={(e) => linkHover(e, true)} onMouseLeave={(e) => linkHover(e, false)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        </a>
        <span style={{ color: BRAND.muted, fontSize: isMobile ? 12 : 13 }} aria-hidden>|</span>
        <a href="https://www.youtube.com/@FundamentalSignals" target="_blank" rel="noopener noreferrer" aria-label="YouTube" style={{ ...linkStyle, border: "none", padding: 0, display: "inline-flex", alignItems: "center" }} onMouseEnter={(e) => linkHover(e, true)} onMouseLeave={(e) => linkHover(e, false)}>
          <svg width="20" height="14" viewBox="0 0 24 17" fill="currentColor" aria-hidden><path d="M23.498 2.67a2.99 2.99 0 0 0-2.105-2.124C19.394 0 12 0 12 0S4.606 0 2.607.546A2.99 2.99 0 0 0 .502 2.67C0 4.69 0 8.5 0 8.5s0 3.81.502 5.83a2.99 2.99 0 0 0 2.105 2.124C4.606 17 12 17 12 17s7.394 0 9.393-.546a2.99 2.99 0 0 0 2.105-2.124C24 12.31 24 8.5 24 8.5s0-3.81-.502-5.83zM9.545 12.14V4.86L15.818 8.5 9.545 12.14z"/></svg>
        </a>
        <div style={{ width: "100%", textAlign: "center", marginTop: 16, color: BRAND.muted, fontSize: isMobile ? 11 : 12 }}>
          © 2025 Fundamental Signals
        </div>
      </footer>
      <LegalModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" content={PRIVACY_POLICY_CONTENT} />
      <LegalModal open={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service" content={TERMS_OF_SERVICE_CONTENT} />
    </>
  );
}

// Public user guide: same shell as landing (logged-out users see LandingHeader)
function GuidePageLayout() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem("authToken");
    const ts = localStorage.getItem("authTimestamp");
    if (token && ts && Date.now() - parseInt(ts, 10) < 24 * 60 * 60 * 1000) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Shell>
      {isAuthenticated ? <Header /> : <LandingHeader isMobile={isMobile} />}
      <div style={{ padding: isMobile ? "8px 16px" : "12px 24px", minHeight: "calc(100vh - 120px)" }}>
        <TutorialPage isAuthenticated={isAuthenticated} />
      </div>
      <Footer isMobile={isMobile} />
    </Shell>
  );
}

const LANDING_FEATURES = [
  { text: "Fundamental metrics & financial statements (income, cash flow, balance sheet)", icon: "📊" },
  { text: "Price vs. fundamental correlation over time for any metric", icon: "📈" },
  { text: "Classic & novel indicators (P/E, revenue growth, fundamental divergence RSI, and more)", icon: "📉" },
  { text: "Screener with filters and custom strategies", icon: "🔍" },
  { text: "Portfolio tracking and calculation assumptions", icon: "💼" },
  { text: "Prediction Lab and analyst consensus data", icon: "🔮" },
];

function LandingPage() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = React.useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem("authToken");
    const ts = localStorage.getItem("authTimestamp");
    if (token && ts && Date.now() - parseInt(ts, 10) < 24 * 60 * 60 * 1000) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const onSelect = (item) => { if (item?.symbol) navigate(`/stock/${item.symbol}/indicators`); };

  return (
    <Shell>
      {isAuthenticated ? <Header /> : <LandingHeader isMobile={isMobile} />}
      {/* Hero / Intro */}
      <section style={{ 
        display: "grid", 
        placeItems: "center", 
        padding: isMobile ? "40px 16px" : isTablet ? "60px 20px" : "80px 24px",
        minHeight: isMobile ? "auto" : "calc(100vh - 80px)"
      }}>
        <div style={{ width: "min(800px, 92vw)", textAlign: "center", animation: "heroEntrance 0.45s ease-out forwards" }}>
          <h1 style={{ 
            fontWeight: 800, 
            marginBottom: isMobile ? 16 : 24, 
            fontSize: isMobile ? "clamp(24px, 8vw, 32px)" : "clamp(32px, 5vw, 56px)",
            background: BRAND.gradientAccent,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            padding: isMobile ? "0 8px" : "0"
          }}>
            Fundamental Signals for Smart Investing
          </h1>
          <p style={{
            color: BRAND.textSecondary,
            fontSize: isMobile ? "14px" : "18px",
            marginBottom: isAuthenticated ? (isMobile ? 32 : 48) : 0,
            lineHeight: 1.6,
            maxWidth: "600px",
            margin: `0 auto ${isAuthenticated ? (isMobile ? 32 : 48) : 24}px`,
            padding: isMobile ? "0 8px" : "0"
          }}>
            Discover data-driven investment opportunities with comprehensive fundamental analysis
          </p>
          {!isAuthenticated && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginBottom: 32 }}>
              <a
                href="#pricing"
                style={{
                  padding: isMobile ? "12px 20px" : "14px 28px",
                  background: BRAND.accent,
                  color: BRAND.text,
                  borderRadius: 12,
                  fontWeight: 600,
                  fontSize: isMobile ? 14 : 16,
                  textDecoration: "none",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 14px rgba(99, 102, 241, 0.4)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = BRAND.accentHover; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(99, 102, 241, 0.5)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = BRAND.accent; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(99, 102, 241, 0.4)"; }}
              >
                View pricing
              </a>
              <Link
                to="/auth"
                style={{
                  padding: isMobile ? "12px 20px" : "14px 28px",
                  background: "transparent",
                  color: BRAND.text,
                  borderRadius: 12,
                  fontWeight: 600,
                  fontSize: isMobile ? 14 : 16,
                  textDecoration: "none",
                  border: `2px solid ${BRAND.border}`,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = BRAND.accent; e.currentTarget.style.color = BRAND.accentLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = BRAND.border; e.currentTarget.style.color = BRAND.text; }}
              >
                Sign in / Sign up
              </Link>
            </div>
          )}
          {isAuthenticated && (
            <div style={{
              background: BRAND.panel,
              border: `1px solid ${BRAND.border}`,
              borderRadius: isMobile ? 16 : 20,
              padding: isMobile ? 16 : 24,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(99, 102, 241, 0.1)",
              transition: "all 0.3s ease"
            }}>
              <SearchBar onSelect={onSelect} autoFocus={!isMobile} placeholder="Search S&P 500 (e.g., AAPL, MSFT)" />
            </div>
          )}
        </div>
      </section>

      {!isAuthenticated && (
        <>
          {/* What you get - above pricing with card grid and visuals */}
          <section style={{
            padding: isMobile ? "48px 16px" : "64px 24px",
            background: BRAND.bgSecondary,
            borderTop: `1px solid ${BRAND.border}`,
          }}>
            <div style={{ maxWidth: 960, margin: "0 auto" }}>
              <h2 style={{
                margin: "0 0 8px",
                fontSize: isMobile ? 24 : 32,
                fontWeight: 700,
                color: BRAND.text,
                textAlign: "center",
                background: BRAND.gradientAccent,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>
                What you get
              </h2>
              <p style={{ margin: "0 0 32px", color: BRAND.textSecondary, fontSize: isMobile ? 14 : 16, textAlign: "center" }}>
                Everything you need for data-driven investing
              </p>
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: isMobile ? 16 : 20,
              }}>
                {LANDING_FEATURES.map((f, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 16,
                      padding: isMobile ? 20 : 24,
                      background: BRAND.panel,
                      border: `1px solid ${BRAND.border}`,
                      borderRadius: 16,
                      transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.35), 0 0 0 1px rgba(99, 102, 241, 0.2)";
                      e.currentTarget.style.borderColor = BRAND.borderLight;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.borderColor = BRAND.border;
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        minWidth: 48,
                        borderRadius: 12,
                        background: "linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.2) 100%)",
                        border: `1px solid ${BRAND.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 24,
                      }}
                    >
                      {f.icon}
                    </div>
                    <p style={{ margin: 0, color: BRAND.textSecondary, fontSize: isMobile ? 14 : 15, lineHeight: 1.6, flex: 1 }}>
                      {f.text}
                    </p>
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        background: BRAND.gradientAccent,
                        opacity: 0.6,
                        borderRadius: "2px 0 0 2px",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section id="pricing" style={{
            padding: isMobile ? "48px 16px" : "64px 24px",
            background: BRAND.bg,
            borderTop: `1px solid ${BRAND.border}`,
          }}>
            <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
              <h2 style={{ margin: "0 0 12px", fontSize: isMobile ? 24 : 32, fontWeight: 700, color: BRAND.text }}>Pricing</h2>
              <p style={{ margin: "0 0 32px", color: BRAND.textSecondary, fontSize: isMobile ? 14 : 16 }}>Choose the plan that fits you. Cancel anytime.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "center" }}>
                <div
                  style={{
                    background: BRAND.panel,
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 16,
                    padding: isMobile ? 24 : 32,
                    minWidth: 260,
                    flex: "1 1 260px",
                    maxWidth: 360,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.4)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600, color: BRAND.text }}>Monthly</h3>
                  <p style={{ margin: "0 0 20px", fontSize: 28, fontWeight: 800, color: BRAND.accentLight }}>$19.99<span style={{ fontSize: 16, fontWeight: 500, color: BRAND.textSecondary }}>/month</span></p>
                  <p style={{ margin: "0 0 24px", fontSize: 14, color: BRAND.textSecondary }}>Full access to all features. Billed monthly.</p>
                  <Link
                    to="/auth"
                    style={{
                      display: "inline-block",
                      padding: "12px 24px",
                      background: BRAND.panelHover,
                      color: BRAND.text,
                      borderRadius: 12,
                      fontWeight: 600,
                      fontSize: 14,
                      textDecoration: "none",
                      border: `1px solid ${BRAND.border}`,
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = BRAND.accent; e.currentTarget.style.borderColor = BRAND.accent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = BRAND.panelHover; e.currentTarget.style.borderColor = BRAND.border; }}
                  >
                    Get started
                  </Link>
                </div>
                <div
                  style={{
                    position: "relative",
                    background: BRAND.panel,
                    border: `2px solid ${BRAND.accent}`,
                    borderRadius: 16,
                    padding: isMobile ? 24 : 32,
                    minWidth: 260,
                    flex: "1 1 260px",
                    maxWidth: 360,
                    boxShadow: `0 0 0 1px ${BRAND.accent}`,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 12px 32px rgba(0,0,0,0.4), 0 0 0 1px ${BRAND.accent}`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 0 0 1px ${BRAND.accent}`; }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: -10,
                      right: 16,
                      padding: "4px 12px",
                      background: BRAND.accent,
                      color: BRAND.text,
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Best value
                  </span>
                  <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600, color: BRAND.text }}>Yearly</h3>
                  <p style={{ margin: "0 0 20px", fontSize: 28, fontWeight: 800, color: BRAND.accentLight }}>$14.99<span style={{ fontSize: 16, fontWeight: 500, color: BRAND.textSecondary }}>/month</span></p>
                  <p style={{ margin: "0 0 24px", fontSize: 14, color: BRAND.textSecondary }}>Billed annually. Save compared to monthly.</p>
                  <Link
                    to="/auth"
                    style={{
                      display: "inline-block",
                      padding: "12px 24px",
                      background: BRAND.accent,
                      color: BRAND.text,
                      borderRadius: 12,
                      fontWeight: 600,
                      fontSize: 14,
                      textDecoration: "none",
                      border: `1px solid ${BRAND.accent}`,
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = BRAND.accentHover; e.currentTarget.style.borderColor = BRAND.accentHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = BRAND.accent; e.currentTarget.style.borderColor = BRAND.accent; }}
                  >
                    Subscribe
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      <Footer isMobile={isMobile} />
    </Shell>
  );
}

/** `/stock/:ticker` → `/stock/:ticker/indicators` (hooks-safe redirect route). */
function StockRedirectToIndicators() {
  const { ticker } = useParams();
  return <Navigate to={`/stock/${ticker}/indicators`} replace />;
}

function ChartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { ticker } = useParams();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [indModal, setIndModal] = React.useState(false);
  const [metModal, setMetModal] = React.useState(false);
  const [customStrategyModal, setCustomStrategyModal] = React.useState(false);

  // Mode: fundamentals = correlation + metrics; indicators = indicator overlays
  const mode = location.pathname.endsWith("/fundamentals") ? "fundamentals" : "indicators";

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onSelect = (item) => {
    if (!item?.symbol) return;
    navigate(`/stock/${item.symbol}/${mode}`);
  };

  const linkStyle = {
    ...{
      border: `1px solid ${BRAND.border}`,
      background: BRAND.panel,
      padding: isMobile ? "10px 14px" : "10px 20px",
      borderRadius: 12,
      textDecoration: "none",
      fontWeight: 600,
      fontSize: isMobile ? "12px" : "14px",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
      color: BRAND.text,
      minHeight: "44px",
      display: "flex",
      alignItems: "center",
    },
  };
  const buttonStyle = {
    border: `1px solid ${BRAND.border}`,
    background: BRAND.panel,
    padding: isMobile ? "10px 14px" : "10px 20px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: isMobile ? "12px" : "14px",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
    color: BRAND.text,
    minHeight: "44px",
    display: "flex",
    alignItems: "center",
  };

  const hoverInactive = (e) => {
    if (!isMobile) {
      e.currentTarget.style.background = BRAND.panelHover;
      e.currentTarget.style.borderColor = BRAND.accent;
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)";
    }
  };

  return (
    <Shell>
      <Header />
      {/* Search bar + context actions */}
      <div style={{
        padding: isMobile ? "12px 16px" : "16px 24px",
        borderBottom: `1px solid ${BRAND.border}`,
        display: "flex",
        gap: isMobile ? 12 : 16,
        alignItems: "center",
        background: BRAND.bgSecondary,
        backdropFilter: "blur(10px)",
        flexWrap: "wrap",
        position: "relative",
        zIndex: 100,
      }}>
        <div style={{ flex: 1, maxWidth: isMobile ? "100%" : 600, minWidth: 0, position: "relative", zIndex: 10001 }}>
          <SearchBar onSelect={onSelect} initialValue={ticker} placeholder="Change ticker…" />
        </div>
        {mode === "fundamentals" && (
          <button
            style={buttonStyle}
            onClick={() => setMetModal(true)}
            onMouseEnter={(e) => hoverInactive(e)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, buttonStyle)}
          >
            Choose metric
          </button>
        )}
        {mode === "indicators" && (
          <div style={{ display: "flex", gap: isMobile ? 8 : 12, flexWrap: "wrap", alignItems: "center" }}>
            <button
              style={buttonStyle}
              onClick={() => setIndModal(true)}
              onMouseEnter={(e) => { if (!isMobile) { e.currentTarget.style.background = BRAND.panelHover; e.currentTarget.style.borderColor = BRAND.accent; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)"; } }}
              onMouseLeave={(e) => Object.assign(e.currentTarget.style, buttonStyle)}
            >
              {isMobile ? "Ind." : "Indicators"}
            </button>
            <button
              style={buttonStyle}
              onClick={() => setMetModal(true)}
              onMouseEnter={(e) => { if (!isMobile) { e.currentTarget.style.background = BRAND.panelHover; e.currentTarget.style.borderColor = BRAND.accent; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)"; } }}
              onMouseLeave={(e) => Object.assign(e.currentTarget.style, buttonStyle)}
            >
              {isMobile ? "Fund." : "Overlay a Fundamental"}
            </button>
            <button
              style={buttonStyle}
              onClick={() => setCustomStrategyModal(true)}
              onMouseEnter={(e) => { if (!isMobile) { e.currentTarget.style.background = BRAND.panelHover; e.currentTarget.style.borderColor = BRAND.accent; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)"; } }}
              onMouseLeave={(e) => Object.assign(e.currentTarget.style, buttonStyle)}
            >
              {isMobile ? "Strategy" : "Custom Strategy"}
            </button>
          </div>
        )}
      </div>
      <div style={{ padding: isMobile ? "12px" : "24px" }}>
        <StockChart
          ticker={ticker}
          mode={mode}
          indModal={indModal}
          setIndModal={setIndModal}
          metModal={metModal}
          setMetModal={setMetModal}
          customStrategyModal={customStrategyModal}
          setCustomStrategyModal={setCustomStrategyModal}
        />
      </div>
      <Footer isMobile={isMobile} />
    </Shell>
  );
}

function AppLayout({ children }) {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <Shell>
      <Header />
      <div style={{ padding: isMobile ? "8px 12px" : "12px 24px", minHeight: "calc(100vh - 80px)" }}>
        {children}
      </div>
      <Footer isMobile={isMobile} />
    </Shell>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    // Check if user is authenticated
    const authToken = localStorage.getItem("authToken");
    const authTimestamp = localStorage.getItem("authTimestamp");
    
    if (authToken && authTimestamp) {
      // Check if token is still valid (24 hours)
      const tokenAge = Date.now() - parseInt(authTimestamp, 10);
      const twentyFourHours = 24 * 60 * 60 * 1000;
      
      if (tokenAge < twentyFourHours) {
        setIsAuthenticated(true);
      } else {
        // Token expired, clear it
        localStorage.removeItem("authToken");
        localStorage.removeItem("authTimestamp");
      }
    }
    
    setIsChecking(false);
  }, []);

  const handleLogin = (success) => {
    setIsAuthenticated(success);
  };

  if (isChecking) {
    // Show loading state while checking authentication
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: BRAND.bg,
          color: BRAND.text,
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return children;
}

function AuthRoutePage() {
  const navigate = useNavigate();
  React.useEffect(() => {
    const token = localStorage.getItem("authToken");
    const ts = localStorage.getItem("authTimestamp");
    if (token && ts && Date.now() - parseInt(ts, 10) < 24 * 60 * 60 * 1000) {
      navigate("/", { replace: true });
    }
  }, [navigate]);
  return <AuthPage />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthRoutePage />} />
      <Route
        path="/select-plan"
        element={
          <ProtectedRoute>
            <PlanSelectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/:ticker"
        element={
          <ProtectedRoute>
            <StockRedirectToIndicators />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/:ticker/fundamentals"
        element={
          <ProtectedRoute>
            <ChartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/:ticker/indicators"
        element={
          <ProtectedRoute>
            <ChartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <AppLayout>
              <div style={{ padding: "8px 0 12px" }}>
                <PortfolioPage />
              </div>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <div style={{ padding: "8px 0 12px" }}>
                <ProfilePage />
              </div>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calculations"
        element={
          <ProtectedRoute>
            <AppLayout>
              <div style={{ padding: "8px 0 12px" }}>
                <CalculationAssumptionsPage />
              </div>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/intrinsic-value"
        element={
          <ProtectedRoute>
            <AppLayout>
              <div style={{ padding: "8px 0 12px" }}>
                <IntrinsicValuePage />
              </div>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ml"
        element={
          <ProtectedRoute>
            <AppLayout>
              <div style={{ padding: "8px 0 12px" }}>
                <PredictionLab />
              </div>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/guide" element={<GuidePageLayout />} />
    </Routes>
  );
}
